import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import { Search, ShoppingCart } from "lucide-react";
import { SiteBrandMark } from "@/components/branding/SiteBrandMark";
import { useCurrentUser } from "@/lib/auth";
import { aclRuleForPath, isAllowed } from "@/lib/acl";
import { useBranding } from "@/lib/branding/useBranding";
import { AccountMenu } from "@/components/AccountMenu";
import { AdminDevToolsMenu } from "@/components/AdminDevToolsMenu";
import { DevRoleSwitcher } from "@/components/DevRoleSwitcher";
import { QuickSaleModal } from "@/components/pos/QuickSaleModal";
import { ThemeToggle } from "@/theme/ThemeToggle";
import { usePageHeader } from "@/layout/usePageHeader";
import { useNav } from "@/navigation/navContext";
import { ProgramSetupIndicator } from "@/components/program-setup/ProgramSetupIndicator";
import { TourHelpButton } from "@/components/tours/TourHelpButton";

export function Header() {
  const { user } = useCurrentUser();
  const { pathname } = useLocation();
  const { meta } = usePageHeader();
  const { openCommandPalette, toggleSidebar } = useNav();
  const { siteName } = useBranding();
  const pageTitle = meta?.title?.trim() || siteName;
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canQuickSale = isAllowed(groups, aclRuleForPath("/pos"));
  const showQuickSale = canQuickSale && pathname !== "/pos";
  const [quickSaleOpen, setQuickSaleOpen] = useState(false);

  useEffect(() => {
    setQuickSaleOpen(false);
  }, [pathname]);

  return (
    <header className="eh-app-header sticky top-0 z-30 shrink-0 border-b border-separator bg-app lg:bg-app/90 lg:backdrop-blur-md">
      <div className="flex h-[var(--eh-app-header-height)] items-center gap-2 px-3 sm:gap-3 sm:px-4 lg:px-5">
        <button
          type="button"
          onClick={toggleSidebar}
          className="hidden rounded-lg p-2 text-label-secondary hover:bg-fill hover:text-label lg:inline-flex"
          aria-label="Navigation ein- oder ausklappen"
        >
          <span className="sr-only">Menü</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
            <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
          </svg>
        </button>

        <SiteBrandMark
          showName={false}
          className="shrink-0 outline-none ring-offset-2 ring-offset-app focus-visible:ring-2 focus-visible:ring-accent lg:hidden"
        />

        <div className="min-w-0 flex-1">
          <h1
            className={clsx(
              "truncate text-sm font-semibold sm:text-base",
              meta?.tone === "danger" && "text-status-red",
              meta?.tone === "warning" && "text-status-orange",
              !meta?.tone && "text-label",
            )}
          >
            {pageTitle}
          </h1>
          {meta?.subtitle ? (
            <p
              className={clsx(
                "truncate text-[0.6875rem] sm:text-xs",
                meta.tone === "danger" && "font-medium text-status-red",
                meta.tone === "warning" && "font-medium text-status-orange",
                !meta.tone && "text-label-secondary",
              )}
            >
              {meta.subtitle}
            </p>
          ) : null}
        </div>

        {showQuickSale ? (
          <button
            type="button"
            onClick={() => setQuickSaleOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={quickSaleOpen}
            aria-label="Schnellverkauf öffnen"
            title="Schnellverkauf"
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-accent px-2.5 text-sm font-medium text-label-on-accent shadow-sm transition hover:bg-accent-hover sm:px-3"
          >
            <ShoppingCart size={16} strokeWidth={2} aria-hidden />
            <span className="hidden md:inline">Schnellverkauf</span>
          </button>
        ) : null}

        <ProgramSetupIndicator />

        <button
          type="button"
          data-tour="header-search"
          onClick={openCommandPalette}
          className="flex min-h-[2.5rem] min-w-[2.5rem] items-center justify-center rounded-lg text-label-secondary transition hover:bg-fill hover:text-label"
          aria-label="Schnellsuche öffnen"
          title="Schnellsuche (Strg+K)"
        >
          <Search size={20} strokeWidth={1.75} aria-hidden />
        </button>

        <div className="hidden min-w-[12rem] flex-1 sm:block lg:max-w-sm xl:max-w-md">
          <button
            type="button"
            onClick={openCommandPalette}
            className="eh-input flex w-full items-center gap-2 py-1.5 text-left text-sm text-label-tertiary"
          >
            <Search size={16} aria-hidden />
            <span>Suchen…</span>
            <kbd className="ml-auto hidden rounded border border-separator px-1 text-[0.625rem] lg:inline">
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <TourHelpButton />
          <AdminDevToolsMenu />
          <DevRoleSwitcher />
          <AccountMenu user={user} />
          <ThemeToggle />
        </div>
      </div>
      {showQuickSale ? (
        <QuickSaleModal open={quickSaleOpen} onClose={() => setQuickSaleOpen(false)} />
      ) : null}
    </header>
  );
}
